import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Mail, Phone, Building, Briefcase, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { StakeholdersBlock as StakeholdersBlockType } from "@/canvas/types/blocks";
import type { Stakeholder } from "@/canvas/types/content";
import { useCanvasStore } from "@/canvas/store";
import { generateItemId } from "@/canvas/utils/id";

interface StakeholdersBlockProps {
  block: StakeholdersBlockType;
  readOnly?: boolean;
}

// Empty form state
const emptyStakeholder = {
  name: "",
  role: "",
  organization: "",
  email: "",
  phone: "",
  responsibilities: "",
};

export function StakeholdersBlock({ block, readOnly = false }: StakeholdersBlockProps) {
  const { t } = useTranslation();
  const updateBlockContent = useCanvasStore((state) => state.updateBlockContent);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyStakeholder);
  const [editingId, setEditingId] = useState<string | null>(null);

  const stakeholders = block.content.stakeholders;

  const handleAddStakeholder = useCallback(() => {
    if (readOnly) return; // Guard against programmatic calls
    setFormData(emptyStakeholder);
    setEditingId(null);
    setIsDialogOpen(true);
  }, [readOnly]);

  const handleEditStakeholder = useCallback(
    (stakeholder: Stakeholder) => {
      if (readOnly) return; // Guard against programmatic calls
      setFormData({
        name: stakeholder.name,
        role: stakeholder.role || "",
        organization: stakeholder.organization || "",
        email: stakeholder.email || "",
        phone: stakeholder.phone || "",
        responsibilities: stakeholder.responsibilities || "",
      });
      setEditingId(stakeholder.id);
      setIsDialogOpen(true);
    },
    [readOnly],
  );

  // Note: V1 store's updateBlockContent expects BlockContent which doesn't include
  // stakeholders type. Cast to any during V1->V2 transition (store update in Phase 27-03).
  const handleDeleteStakeholder = useCallback(
    (id: string) => {
      if (readOnly) return; // Guard against programmatic calls
      updateBlockContent(block.id, {
        stakeholders: stakeholders.filter((s) => s.id !== id),
      } as unknown as Parameters<typeof updateBlockContent>[1]);
    },
    [block.id, stakeholders, updateBlockContent, readOnly],
  );

  const handleSave = useCallback(() => {
    if (!formData.name.trim()) return;

    if (editingId) {
      // Update existing
      updateBlockContent(block.id, {
        stakeholders: stakeholders.map((s) =>
          s.id === editingId
            ? {
                ...s,
                name: formData.name.trim(),
                role: formData.role.trim() || undefined,
                organization: formData.organization.trim() || undefined,
                email: formData.email.trim() || undefined,
                phone: formData.phone.trim() || undefined,
                responsibilities: formData.responsibilities.trim() || undefined,
              }
            : s,
        ),
      } as unknown as Parameters<typeof updateBlockContent>[1]);
    } else {
      // Add new
      const newStakeholder: Stakeholder = {
        id: generateItemId(),
        name: formData.name.trim(),
        role: formData.role.trim() || undefined,
        organization: formData.organization.trim() || undefined,
        email: formData.email.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        responsibilities: formData.responsibilities.trim() || undefined,
      };
      updateBlockContent(block.id, {
        stakeholders: [...stakeholders, newStakeholder],
      } as unknown as Parameters<typeof updateBlockContent>[1]);
    }

    setIsDialogOpen(false);
    setFormData(emptyStakeholder);
    setEditingId(null);
  }, [block.id, stakeholders, formData, editingId, updateBlockContent]);

  return (
    <div>
      {/* Stakeholder chips with inline add button */}
      <div className="flex flex-wrap items-center gap-2">
        {stakeholders.map((stakeholder) => (
          <Popover key={stakeholder.id}>
            <PopoverTrigger asChild>
              <Badge
                variant="secondary"
                className="cursor-pointer hover:bg-secondary/80 transition-colors text-foreground/90"
              >
                <User className="size-3.5 mr-1.5" />
                {stakeholder.name}
              </Badge>
            </PopoverTrigger>
            <PopoverContent
              className="w-72 px-0 pb-0 text-ui-body-sm text-muted-foreground"
              align="start"
            >
              <div className="space-y-3 px-4">
                <div className="text-ui-title-3 text-foreground">{stakeholder.name}</div>

                {stakeholder.role && (
                  <div className="flex items-center gap-2">
                    <Briefcase className="size-4 shrink-0" />
                    <span>{stakeholder.role}</span>
                  </div>
                )}

                {stakeholder.organization && (
                  <div className="flex items-center gap-2">
                    <Building className="size-4 shrink-0" />
                    <span>{stakeholder.organization}</span>
                  </div>
                )}

                {stakeholder.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="size-4 shrink-0 text-muted-foreground" />
                    <a
                      href={`mailto:${stakeholder.email}`}
                      className="text-primary hover:underline"
                    >
                      {stakeholder.email}
                    </a>
                  </div>
                )}

                {stakeholder.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="size-4 shrink-0 text-muted-foreground" />
                    <a href={`tel:${stakeholder.phone}`} className="text-primary hover:underline">
                      {stakeholder.phone}
                    </a>
                  </div>
                )}

                {stakeholder.responsibilities && (
                  <div className="pt-2 border-t">{stakeholder.responsibilities}</div>
                )}
              </div>
              {!readOnly && (
                <div className="flex justify-between gap-2 py-3 border-t px-4 mt-4">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDeleteStakeholder(stakeholder.id)}
                  >
                    {t("common.delete", "Delete")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEditStakeholder(stakeholder)}
                  >
                    {t("common.edit", "Edit")}
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        ))}

        {/* Add button - inline, subtle icon-only style, hidden when readOnly */}
        {!readOnly && (
          <button
            onClick={handleAddStakeholder}
            className="inline-flex items-center gap-0.5 px-2 py-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            aria-label={t("canvas.blocks.stakeholders.add", "Add Stakeholder")}
          >
            <User className="h-3.5 w-3.5" />
            <Plus className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Empty state - only shown when no stakeholders */}
      {stakeholders.length === 0 && (
        <div className="rounded-lg p-6 text-center bg-muted/30 mt-2">
          <User className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">
            {t("canvas.blocks.stakeholders.empty", "No stakeholders added yet")}
          </p>
        </div>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingId
                ? t("canvas.blocks.stakeholders.editTitle", "Edit Stakeholder")
                : t("canvas.blocks.stakeholders.addTitle", "Add Stakeholder")}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t("canvas.blocks.stakeholders.fields.name", "Name")} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder={t("canvas.blocks.stakeholders.placeholders.name", "John Smith")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">{t("canvas.blocks.stakeholders.fields.role", "Role")}</Label>
              <Input
                id="role"
                value={formData.role}
                onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
                placeholder={t("canvas.blocks.stakeholders.placeholders.role", "Project Manager")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="organization">
                {t("canvas.blocks.stakeholders.fields.organization", "Organization")}
              </Label>
              <Input
                id="organization"
                value={formData.organization}
                onChange={(e) => setFormData((prev) => ({ ...prev, organization: e.target.value }))}
                placeholder={t(
                  "canvas.blocks.stakeholders.placeholders.organization",
                  "Company AB",
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">
                  {t("canvas.blocks.stakeholders.fields.email", "Email")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="name@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">
                  {t("canvas.blocks.stakeholders.fields.phone", "Phone")}
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="+46 70 123 4567"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="responsibilities">
                {t("canvas.blocks.stakeholders.fields.responsibilities", "Responsibilities")}
              </Label>
              <Textarea
                id="responsibilities"
                value={formData.responsibilities}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, responsibilities: e.target.value }))
                }
                placeholder={t(
                  "canvas.blocks.stakeholders.placeholders.responsibilities",
                  "Key responsibilities and areas of expertise...",
                )}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button onClick={handleSave} disabled={!formData.name.trim()}>
              {editingId ? t("common.save", "Save") : t("common.add", "Add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
